import React, { useState, useEffect, useRef } from 'react'; 
import { useFormik } from 'formik'; 
import * as Yup from 'yup'; 
import { getDocs, query, collection, where } from 'firebase/firestore'; 
import { db } from './firebase'; 
import styled from 'styled-components';  
import { MdPersonSearch } from 'react-icons/md';

// Funkce pro načtení uživatelských dat
const fetchUserData = async (id) => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('ID', '==', id));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        console.log('Uživatelská data:', userData); // Logování pro kontrolu 

        // Vraťte data uživatele
        return { id: userDoc.id, email: userData.email || 'Není k dispozici', ...userData };
    }

    return null;
};

const SeznamUzivatelu = ({ closePopup }) => {
    const [userData, setUserData] = useState(null);
    const [serverError, setServerError] = useState('');

    const inputRef = useRef(null);

    const formik = useFormik({
        initialValues: {
            id: ''
        },
        validationSchema: Yup.object({
            id: Yup.string().required('ID je povinné').length(5, 'ID musí mít přesně 5 číslic')
        }),
        onSubmit: async () => {
            try {
                const data = await fetchUserData(formik.values.id);
                if (data) {
                    setUserData(data);
                    setServerError('');
                } else {
                    setServerError('Uživatel s tímto ID nebyl nalezen.');
                    setUserData(null);
                }
            } catch (error) {
                console.error('Chyba při načítání uživatele:', error);
                setServerError('Došlo k chybě při načítání uživatele.');
                setUserData(null);
            }
        }
    });

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    return (
        <ModalOverlay>
            <ModalContent>
                <ModalTitle><MdPersonSearch size={20} style={{ color:'#cde' }} />HLEDAT UŽIVATELE PODLE ID</ModalTitle>
                <form onSubmit={formik.handleSubmit}>
                    <ModalInput
                        ref={inputRef}
                        type="text"
                        name="id"
                        value={formik.values.id}
                        placeholder="Zadejte ID uživatele"
                        autoComplete="off"
                        autoCorrect="off"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{ borderColor: formik.touched.id && formik.errors.id ? '#e74c3c' : '#ccc' }}
                    />
                    {formik.touched.id && formik.errors.id ? (
                        <ErrorText>{formik.errors.id}</ErrorText>
                    ) : null}
                    {serverError && (
                        <ErrorText>{serverError}</ErrorText>
                    )}
                    <ModalButtons>
                        <ModalButton type="submit">Vyhledat</ModalButton>
                        <ModalButton type="button" onClick={closePopup}>Zavřít</ModalButton>
                    </ModalButtons>
                </form>
                {userData && (
                    <UserInfo>
                        <UserDetail>ID: {userData.ID}</UserDetail>
                        <UserDetail>Email: {userData.email}</UserDetail>
                        <UserDetail>Role: {userData.role}</UserDetail>

                        <VipInfoWrapper>
                            {userData.vip && userData.vip.permanent && (
                                <VipInfoLabel>VIP (Permanentní)</VipInfoLabel>
                            )}
                            {userData.vip && !userData.vip.permanent && (
                                <VipInfo>
                                    <strong style={{ color: '#ffd54a' }}>VIP</strong>
                                    <div style={{ fontSize: 12, color: '#cfd8e3' }}>
                                        {userData.vip.expiresAt
                                            ? `Platnost do: ${new Date(userData.vip.expiresAt).toLocaleDateString()}`
                                            : 'Platnost neurčena'}
                                    </div>
                                </VipInfo>
                            )}
                        </VipInfoWrapper>
                    </UserInfo>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};

// replace existing formatVipInfo with this improved version
function formatVipInfo(vip) {
  if (!vip) return null;
  if (vip.permanent) return { label: 'VIP (Permanentní)', detail: null };

  let expiresDate = null;
  const e = vip.expiresAt ?? null;

  const tryParseNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const parseFromSeconds = (sec, nano) => {
    const s = tryParseNumber(sec);
    const ns = tryParseNumber(nano) || 0;
    if (s != null) return new Date(s * 1000 + Math.floor(ns / 1e6));
    return null;
  };

  const parseFromString = (str) => {
    if (typeof str !== 'string') return null;
    // normalize non-breaking spaces and narrow no-break spaces
    let s = str.replace(/\u00A0|\u202F/g, ' ');
    // remove " at " introduced by Firestore UI and remove trailing UTC offset like " UTC+2"
    s = s.replace(/\s+at\s+/i, ' ').replace(/\s+UTC[+-]?\d+/i, '').trim();
    // try Date.parse
    const p = Date.parse(s);
    if (!isNaN(p)) return new Date(p);
    // try alternative: remove commas like "October 25, 2025 1:32:04 PM"
    const s2 = s.replace(/,\s*/g, ' ');
    const p2 = Date.parse(s2);
    if (!isNaN(p2)) return new Date(p2);
    return null;
  };

  try {
    if (e) {
      // Firestore Timestamp instance
      if (typeof e.toDate === 'function') {
        expiresDate = e.toDate();
      }
      // Firestore-like with seconds/nanoseconds (numbers or strings)
      else if (typeof e.seconds !== 'undefined') {
        expiresDate = parseFromSeconds(e.seconds, e.nanoseconds ?? e._nanoseconds);
      }
      // alternate keys _seconds/_nanoseconds
      else if (typeof e._seconds !== 'undefined') {
        expiresDate = parseFromSeconds(e._seconds, e._nanoseconds ?? e.nanoseconds);
      }
      // numeric timestamp (ms or s)
      else if (typeof e === 'number') {
        expiresDate = e > 1e12 ? new Date(e) : new Date(e * 1000);
      }
      // string date (try robust parsing)
      else if (typeof e === 'string') {
        expiresDate = parseFromString(e);
      }
    }

    // fallback: if expiresAt missing but durationDays present
    if (!expiresDate && vip.durationDays) {
      const d = tryParseNumber(vip.durationDays);
      if (d != null) expiresDate = new Date(Date.now() + d * 24 * 60 * 60 * 1000);
    }
  } catch (err) {
    console.warn('formatVipInfo parse error', err, vip);
    expiresDate = null;
  }

  if (!expiresDate || isNaN(expiresDate.getTime())) return { label: 'VIP', detail: 'Platnost neurčena' };

  const msLeft = expiresDate.getTime() - Date.now();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const dateStr = expiresDate.toLocaleString();

  if (msLeft <= 0) return { label: 'VIP (vypršelo)', detail: `vypršelo ${dateStr}` };
  return { label: 'VIP', detail: `${daysLeft} dní (vyprší: ${dateStr})` };
}

// Stylované komponenty
const ModalOverlay = styled.div`
    cursor: default;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2147483000;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 520px;
    max-width: calc(100vw - 48px);
    padding: 20px 22px;
    border-radius: 12px;
    box-sizing: border-box;
    background: #071014;
    color: #fff;
    cursor: default;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    border: 1px solid rgba(255,255,255,0.04);
`;

const ModalTitle = styled.h2`
    margin: 6px 0 18px 0;
    font-size: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
`;

const ModalInput = styled.input`
    margin-bottom: 8px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.08);
    width: 100%;
    font-size: 16px;
    box-sizing: border-box;
    background: rgba(255,255,255,0.02);
    color: #fff;
    &:focus {
        border-color: #2ecc71;
        box-shadow: 0 6px 18px rgba(46, 204, 113, 0.06);
        outline: none;
    }
`;

const ErrorText = styled.p`
    color: #ff6b6b;
    margin: 6px 0 0 0;
    font-size: 14px;
    text-align: left;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 18px;
`;

const ModalButton = styled.button`
    background: ${props => props.variant === 'secondary' ? '#2b2f33' : '#22c55e'};
    border: none;
    color: #fff;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    transition: transform .12s ease, filter .12s ease;
    &:hover { filter: brightness(1.06); transform: translateY(-2px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

const UserInfo = styled.div`
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
`;

const UserDetail = styled.p`
    font-size: 15px;
    margin: 0;
    background-color: rgba(255,255,255,0.03);
    color: #cfe6ff;
    padding: 10px 12px;
    border-radius: 8px;
    width: 100%;
    text-align: left;
    box-sizing: border-box;
`;

const VipInfoWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  text-align: center;
`;

const VipInfoLabel = styled.div`
  font-weight: 700;
  color: #ffd54a;
`;

const VipInfo = styled.div`
  font-size: 12px;
  color: #cfd8e3;
`;

const UserRow = styled.div`
  padding: 12px;
  border-radius: 8px;
  background: #131417;
  margin-bottom: 10px;
`;

const UserName = styled.div` font-weight: 700; color: #fff; `;
const UserRole = styled.div` color: #9fb0d6; font-size: 13px;
`;

export default SeznamUzivatelu;
